import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Package, Truck, Phone, Mic, CheckCircle2, Loader2, CalendarCheck, Camera, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate } from "@/constants";
import { Button } from "@/components/ui/button";

const ACTIONS = {
  delivery: "delivery_departure",
  pickup: "pickup_departure",
  agb_hand: "agb_handed",
  agb_return: "agb_returned",
  cabine_hand: "cabine_handed",
  cabine_return: "cabine_returned",
};

const CATEGORIES = {
  livraison: {
    tag: "Livraison Photobooth54",
    Icon: Camera,
    wrap: "border-l-4 border-l-sky-500",
    chip: "bg-sky-50 text-sky-700 border-sky-200",
    iconCls: "text-sky-600",
  },
  retrait: {
    tag: "Retrait à Lesménils",
    Icon: MapPin,
    wrap: "border-l-4 border-l-amber-500",
    chip: "bg-amber-50 text-amber-700 border-amber-200",
    iconCls: "text-amber-600",
  },
};

const TASK_UI = {
  delivery: { Icon: Package, title: (t) => `${t.has_agb ? "Photobooth + Audio Guest Book" : "Photobooth"} — ${t.city || "Livraison"}${t.booth_name ? ` · ${t.booth_name}` : ""}`, cta: "VALIDER LE DÉPART EN LIVRAISON" },
  pickup: { Icon: Truck, title: (t) => `Reprise photobooth — ${t.city || ""}${t.booth_name ? ` · ${t.booth_name}` : ""}`, cta: "VALIDER LE DÉPART EN REPRISE" },
  agb_hand: { Icon: Phone, title: (t) => `Audio Guest Book — ${t.client} attendu`, cta: "MATÉRIEL REMIS AU CLIENT" },
  agb_return: { Icon: Phone, title: (t) => `Retour Audio Guest Book — ${t.client}`, cta: "MATÉRIEL RETOURNÉ" },
  cabine_hand: { Icon: Mic, title: (t) => `Cabine Audio Guest Book — ${t.client} attendu`, cta: "CABINE REMISE AU CLIENT" },
  cabine_return: { Icon: Mic, title: (t) => `Retour Cabine Audio Guest Book — ${t.client}`, cta: "CABINE RETOURNÉE" },
};

function TaskCard({ t, acting, onAct, onOpen }) {
  const ui = TASK_UI[t.task];
  if (!ui) return null;
  const cat = CATEGORIES[t.category] || CATEGORIES.livraison;
  const key = `${t.task}-${t.reservation_id}`;
  const ready = t.task !== "delivery" || t.checklist_total === 0 || t.checklist_checked === t.checklist_total;
  return (
    <div data-testid={`task-${key}`} className={`rounded-xl border border-slate-200 bg-white p-3.5 space-y-2.5 ${cat.wrap}`}>
      <div className="flex items-center justify-between gap-2">
        <span data-testid={`task-category-${key}`} className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider ${cat.chip}`}>
          <cat.Icon className="h-3 w-3" /> {cat.tag}
        </span>
        {t.slot && <span data-testid={`task-slot-${key}`} className="text-xs font-semibold text-slate-600">{t.slot}</span>}
      </div>
      <p className="flex items-center gap-2 font-semibold text-sm">
        <ui.Icon className={`h-4 w-4 shrink-0 ${cat.iconCls}`} />
        <span className="truncate">{ui.title(t)}</span>
      </p>
      <p className="text-xs text-slate-500 pl-6">
        {t.client}{t.datetime ? ` · ${format(new Date(t.datetime), "HH'h'mm")}` : ""}
      </p>
      {t.task === "delivery" && t.checklist_total > 0 && (
        <div className="pl-6 space-y-1">
          <p data-testid={`task-checklist-${key}`} className={`text-xs font-semibold ${ready ? "text-emerald-600" : "text-amber-600"}`}>
            Checklist : {t.checklist_checked}/{t.checklist_total} {ready ? "✓" : "— préparation en cours"}
          </p>
          {!ready && (
            <p className="text-xs text-amber-700">
              Manquant : {(t.missing || []).join(", ")} —{" "}
              <button type="button" data-testid={`task-open-${key}`} className="underline font-medium" onClick={() => onOpen(t.reservation_id)}>ouvrir la réservation</button>
            </p>
          )}
        </div>
      )}
      <div className="pl-6">
        <Button
          data-testid={`task-validate-${key}`}
          disabled={acting === key || !ready}
          onClick={() => onAct(t)}
          className={`w-full sm:w-auto font-semibold transition-colors ${ready ? "bg-slate-900 hover:bg-slate-800 text-white" : ""}`}
          variant={ready ? "default" : "outline"}
        >
          {acting === key && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
          {ui.cta}
        </Button>
      </div>
    </div>
  );
}

export default function WeekendTasks() {
  const [data, setData] = useState(null);
  const [acting, setActing] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/stats/weekend-tasks").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!data) return null;

  const act = async (t) => {
    const key = `${t.task}-${t.reservation_id}`;
    setActing(key);
    try {
      await api.post(`/reservations/${t.reservation_id}/actions`, { action: ACTIONS[t.task] });
      toast.success("Action validée — enregistrée dans l'historique");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action impossible");
    } finally {
      setActing(null);
    }
  };

  const groups = [];
  for (const t of data.tasks || []) {
    const day = t.datetime ? format(new Date(t.datetime), "EEEE d MMMM yyyy", { locale: fr }) : "À planifier";
    const g = groups.find(([d]) => d === day);
    if (g) g[1].push(t); else groups.push([day, [t]]);
  }

  return (
    <section data-testid="weekend-tasks" className="rounded-2xl border-2 border-slate-900 bg-white p-4 sm:p-5 space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <CalendarCheck className="h-5 w-5 text-slate-900" />
        <h2 className="font-heading text-lg font-bold tracking-tight uppercase">Ce week-end</h2>
        {data.weekend && (
          <span data-testid="weekend-range" className="text-xs text-slate-500">
            du {fmtDate(data.weekend.start)} au {fmtDate(data.weekend.end)}
          </span>
        )}
      </div>
      {!data.weekend ? (
        <p data-testid="weekend-none" className="text-sm text-slate-400">Aucune opération prévue pour le moment. L'agenda reste disponible pour le futur.</p>
      ) : data.all_done ? (
        <p data-testid="weekend-all-done" className="flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm font-semibold text-emerald-700">
          <CheckCircle2 className="h-5 w-5 shrink-0" /> Toutes les opérations du week-end sont terminées.
        </p>
      ) : (
        <div className="space-y-4">
          {groups.map(([day, tasks]) => (
            <div key={day} data-testid={`weekend-day-${day}`}>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">{day}</p>
              <div className="space-y-2">
                {tasks.map((t) => (
                  <TaskCard key={`${t.task}-${t.reservation_id}`} t={t} acting={acting} onAct={act} onOpen={(rid) => navigate(`/reservations/${rid}`)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
